<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from 'vue';
import i18n from '@/localisation/i18n';
import TraderUserDialog from '@/modules/trader-admin/components/TraderUserDialog.vue';
import PermissionValidator, { Role } from '@/utils/helpers/permissions';
import { RestOperation } from '@/utils/helpers/rest';
import { errorString, i18nServerMessage } from '@/utils/helpers/rest-response';
import { AgTableClient } from '@/modules/common/components/ag-table';
import * as cols from '@/modules/common/components/ag-table/columns/users';
import { ColDef } from 'ag-grid-enterprise';
import { User, UserListResponse, UserRequest } from '@/modules/user-accounts/models';
import axios, { AxiosError } from 'axios';
import { ApiError } from '@/utils/errors/api-error';
import { useDialogManager } from '@/plugins/dialog-manager';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import logger from '@/modules/common/services/logger.service';
import { useSocketStore } from '@/store/socket-store';
import { store as vuex } from '@/store/store';

const dialog = useDialogManager();
const snackbar = useSnackbarManager();
const socketStore = useSocketStore();

socketStore.useEventHandler('refreshUsers', async () => {
  await fetchTraderAdminUsers();
});

const defaultUser = UserRequest.create();

const loginState = computed(() => vuex.state.loginState);

// Local state
const companyUsers = shallowRef<User[]>([]);
const tableSearch = ref('');
const setQuickFilterText = shallowRef<(text: string) => void>(() => {
  /* noop */
});

const addBuffer = ref<UserRequest | null>(null);
const dialogBuffer = ref<UserRequest | null>(null as UserRequest | null);
const dialogOptions = ref({
  isActive: false,
  crudAction: RestOperation.Noop,
  title: 'User',
  adminCount: 0,
  cancelOptions: ['escape', 'outside'], // no 'X' for real FORMs
});

const columnDefs = computed<ColDef[]>(() => [
  cols.name(),
  cols.company(),
  cols.email(),
  cols.role(),
  cols.traderAccountStatus(),
  cols.tfa(),
  cols.riskLimits(),
  cols.actions({
    editUser: editUser,
    deleteUser: deleteUser,
    disableUser: disableUser,
    enableUser: enableUser,
  }),
]);

function onReady(config: { setQuickFilterText: (text: string) => void }): void {
  setQuickFilterText.value = config.setQuickFilterText;
}

async function fetchTraderAdminUsers(): Promise<void> {
  try {
    const { data } = await axios.get('/api/1/trader-admin/users');
    const response = UserListResponse.fromData(data);
    companyUsers.value = response.users;
  } catch (e) {
    throw new ApiError(i18nServerMessage(e as AxiosError));
  }
}

async function deleteTraderAdminUser(user: User): Promise<void> {
  try {
    await axios.delete(`/api/1/trader-admin/users/${user.id}`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be deleted`)
    );
  }
}

async function disableTraderAdminUser(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/trader-admin/users/${user.id}/disable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be disabled`)
    );
  }
}

async function enableTraderAdminUser(user: User): Promise<void> {
  try {
    await axios.put(`/api/1/trader-admin/users/${user.id}/enable`);
  } catch (e) {
    throw new ApiError(
      i18nServerMessage(e as AxiosError, `${user.emailAddress} cannot be enabled`)
    );
  }
}

function getRowId(userAccount: User): string {
  return userAccount.id;
}

function createUser(): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Create;
  dialogOptions.value.title = 'Add Company User';
  dialogOptions.value.adminCount = countAdministrators();

  // addBuffer is a 2nd buffer behind the dialog so that the admin
  // can escape from `Create New User` and return with partially filled record
  // browsing the user list will not destroy the partially filled record
  if (addBuffer.value === null) {
    addBuffer.value = UserRequest.clone(defaultUser);
  }
  dialogBuffer.value = addBuffer.value;

  // managing users of my company only!
  addBuffer.value.companyID = loginState.value.user!.companyID;
  addBuffer.value.companyTradingPermissions = loginState.value.user!.companyTradingPermissions;
}

function editUser(rowUser: User): void {
  dialogOptions.value.isActive = true;
  dialogOptions.value.crudAction = RestOperation.Update;
  dialogOptions.value.title = 'Update User Profile';
  dialogOptions.value.adminCount = countAdministrators();
  // do not modify the row while typing
  dialogBuffer.value = UserRequest.fromUser(rowUser);
}

function closeModal(): void {
  // wipe user data
  dialogOptions.value.isActive = false;
  dialogOptions.value.crudAction = RestOperation.Noop;
  dialogOptions.value.title = '';
  addBuffer.value = null;
  dialogBuffer.value = null;
}

function deleteUser(rowUser: User): void {
  // can't delete own account if you are the last admin
  if (rowUser.id === loginState.value.user?.id && countAdministrators() === 1) {
    dialog.alert({
      title: i18n.t('adminDeleteUser'),
      icon: 'mdi-alert',
      message: i18n.t('adminDeleteLastAdminAccount', {
        userName: rowUser.name,
        companyName: rowUser.companyName,
        emailAddress: rowUser.emailAddress,
      }),
      shouldManuallyConfirm: true,
    });
    return;
  }

  dialog.ask({
    title: i18n.t('adminDeleteUser'),
    color: 'error',
    icon: 'mdi-alert',
    message: i18n.t('adminDeleteUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminDeleteUser'),
    onAccept: () => doDeleteUser(rowUser),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

function disableUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminDisableUser'),
    color: 'warning',
    icon: 'mdi-account-off',
    message: i18n.t('adminDisableUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminDisableUser'),
    onAccept: () => doDisableUser(rowUser),
    rejectText: i18n.t('cancelButton'),
    shouldManuallyConfirm: true,
  });
}

function enableUser(rowUser: User): void {
  dialog.ask({
    title: i18n.t('adminEnableUser'),
    color: 'info',
    icon: 'mdi-account-check',
    message: i18n.t('adminEnableUserMessage', {
      userName: rowUser.name,
      companyName: rowUser.companyName,
      emailAddress: rowUser.emailAddress,
    }),
    acceptText: i18n.t('adminEnableUser'),
    onAccept: () => doEnableUser(rowUser),
    rejectText: i18n.t('cancelButton'),
  });
}

async function doDeleteUser(rowUser: User) {
  try {
    await deleteTraderAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminDeletedUserMessage'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doDisableUser(rowUser: User) {
  try {
    await disableTraderAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminDisabledUser'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

async function doEnableUser(rowUser: User) {
  try {
    await enableTraderAdminUser(rowUser);

    snackbar.confirm(i18n.tc('adminEnabledUser'));
  } catch (e) {
    snackbar.error(errorString(e as Error));
  }
}

function countAdministrators(): number {
  return companyUsers.value.reduce((count: number, user: User) => {
    const validator = new PermissionValidator(user.roles);
    const hasPermission = validator.hasPermission(Role.TraderAdmin);
    return hasPermission ? count + 1 : count;
  }, 0);
}

onMounted(async () => {
  try {
    await fetchTraderAdminUsers();
  } catch (e) {
    logger.warn(e);
  }
});
</script>

<template>
  <VCard class="flex d-flex flex-column fill-height">
    <!-- edit user popup -->
    <TraderUserDialog
      v-if="dialogOptions.isActive && dialogBuffer"
      :admin-count="dialogOptions.adminCount"
      :crud-action="dialogOptions.crudAction"
      :title="dialogOptions.title"
      :user="dialogBuffer"
      @close-modal="closeModal"
    />
    <VCardTitle>
      <span class="headline">{{ $t('adminSelectUsers.title') }}</span>
    </VCardTitle>
    <VCardSubtitle>
      <span class="text--secondary headline-2">{{ $t('adminSelectUsers.description') }}</span>
    </VCardSubtitle>
    <VCardText>
      <!-- add user account -->
      <VBtn class="ma-6" color="primary" data-test-id="add-user-button" @click="createUser">
        <VIcon left> mdi-plus</VIcon>
        {{ $t('adminAddUser') }}
      </VBtn>

      <!-- search bar -->
      <VTextField
        v-model="tableSearch"
        clearable
        :label="$t('adminSearchUser')"
        prepend-inner-icon="mdi-magnify"
        @input="setQuickFilterText(tableSearch)"
      />
    </VCardText>

    <AgTableClient
      v-if="companyUsers"
      :column-defs="columnDefs"
      :get-row-id="getRowId"
      :page-size="1000"
      :row-data="companyUsers"
      :sort="{ colId: 'name', sort: 'asc' }"
      @ready="onReady"
    />
  </VCard>
</template>

<style lang="scss" scoped></style>
